<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>

    <template v-else-if="item">
      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h3 class="mb-50">
              {{ item.display_name || item.map_number }}
            </h3>
            <div class="text-muted">
              {{ $t('ledger_maps_number') }} {{ item.map_number || '—' }}
              · {{ quadrant(item) }}
            </div>
          </div>
          <div
            v-if="canPost"
            class="d-none d-md-flex flex-wrap mt-1 mt-md-0"
          >
            <b-button
              v-for="action in actions"
              :key="action"
              size="sm"
              class="mr-50 mb-50 rounded-pill"
              :variant="panel === action ? 'primary' : 'outline-primary'"
              @click="panel = panel === action ? null : action"
            >
              {{ $t(`ledger_maps_action_${action}`) }}
            </b-button>
          </div>
        </div>

        <div class="ledger-map-stock mt-1">
          <div>
            <span class="text-muted">{{ $t('ledger_maps_qty') }}</span>
            <strong class="ledger-num d-block">{{ qty(item.qty_on_hand) }}</strong>
          </div>
          <div>
            <span class="text-muted">{{ $t('ledger_maps_unit_cost') }}</span>
            <strong class="ledger-num d-block">{{ money(item.unit_cost) }}</strong>
          </div>
          <div>
            <span class="text-muted">{{ $t('ledger_maps_value') }}</span>
            <strong class="ledger-num d-block">{{ money(item.inventory_value) }}</strong>
          </div>
        </div>
      </b-card>

      <b-card
        v-if="panel && canPost"
        class="rounded-lg"
      >
        <h5>{{ $t(`ledger_maps_action_${panel}`) }}</h5>
        <b-row>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group :label="$t('ledger_maps_qty')">
              <b-form-input
                v-model.number="form.quantity"
                type="number"
                min="0"
                step="0.001"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="panel === 'opening' || panel === 'purchase'"
            cols="6"
            md="3"
          >
            <b-form-group :label="$t('ledger_maps_unit_cost')">
              <b-form-input
                v-model.number="form.unit_cost"
                type="number"
                min="0"
                step="0.01"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="panel === 'sale'"
            cols="6"
            md="3"
          >
            <b-form-group :label="$t('ledger_maps_unit_price')">
              <b-form-input
                v-model.number="form.unit_price"
                type="number"
                min="0"
                step="0.01"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group :label="$t('ledger_date')">
              <b-form-input
                v-model="form.movement_date"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="needsTill"
            cols="12"
            md="6"
          >
            <b-form-group :label="$t('ledger_payroll_till')">
              <v-select
                v-model="form.account_id"
                :dir="isRtl ? 'rtl' : 'ltr'"
                :options="tills"
                :reduce="option => option.id"
                :get-option-label="tillLabel"
                :loading="loadingTills"
                :placeholder="$t('ledger_payroll_till')"
                class="ledger-account-select"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group :label="$t('ledger_description')">
              <b-form-input v-model="form.description" />
            </b-form-group>
          </b-col>
        </b-row>

        <p
          v-if="panel === 'purchase' && wacPreview != null"
          class="text-muted small"
        >
          {{ $t('ledger_maps_wac_preview') }} {{ money(wacPreview) }}
        </p>
        <p
          v-if="panel === 'sale' && salePreview != null"
          class="text-muted small"
        >
          {{ $t('ledger_maps_margin_preview') }} {{ money(salePreview) }}
        </p>

        <b-button
          variant="primary"
          class="rounded-pill"
          :disabled="acting || !canSubmit"
          @click="confirmPost"
        >
          <b-spinner
            v-if="acting"
            small
            class="mr-50"
          />
          {{ $t(`ledger_maps_action_${panel}`) }}
        </b-button>
      </b-card>

      <b-alert
        v-if="saleResult"
        show
        variant="success"
      >
        <div class="mb-50">
          {{ $t('ledger_maps_margin') }}
          <strong class="ledger-num">{{ money(saleMargin(saleResult)) }}</strong>
        </div>
        <b-link
          v-if="journalId(saleResult, 'revenue') && canOpenJournal"
          class="d-inline-block mr-1"
          :to="journalRoute(journalId(saleResult, 'revenue'))"
        >
          {{ $t('ledger_maps_revenue_journal') }}
        </b-link>
        <b-link
          v-if="journalId(saleResult, 'cogs') && canOpenJournal"
          class="d-inline-block"
          :to="journalRoute(journalId(saleResult, 'cogs'))"
        >
          {{ $t('ledger_maps_cogs_journal') }}
        </b-link>
      </b-alert>

      <b-alert
        v-if="showLinksHint"
        show
        variant="warning"
      >
        <b-link :to="{ name: 'ledger-links' }">
          {{ $t('ledger_links_title') }}
        </b-link>
      </b-alert>

      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">
            {{ $t('ledger_maps_recent') }}
          </h5>
          <b-link :to="{ name: 'ledger-maps-movements', query: { cadastral_map_id: item.id } }">
            {{ $t('ledger_maps_movements') }}
          </b-link>
        </div>
        <div
          v-if="!movements.length"
          class="text-muted"
        >
          {{ $t('ledger_maps_movements_empty') }}
        </div>
        <article
          v-for="row in movements"
          :key="row.id"
          class="ledger-map-card"
        >
          <div class="d-flex justify-content-between">
            <b-badge :variant="typeVariant(row.type)">
              {{ typeText(row.type) }}
            </b-badge>
            <span class="text-muted small">{{ row.movement_date }}</span>
          </div>
          <div class="ledger-num mt-50">
            {{ qty(row.quantity) }} · {{ money(row.total_cost) }}
          </div>
          <div v-if="canOpenJournal">
            <b-link
              v-if="journalId(row, 'revenue')"
              class="d-inline-block mr-1"
              :to="journalRoute(journalId(row, 'revenue'))"
            >
              {{ $t('ledger_maps_revenue_journal') }}
            </b-link>
            <b-link
              v-if="journalId(row, 'cogs')"
              class="d-inline-block"
              :to="journalRoute(journalId(row, 'cogs'))"
            >
              {{ $t('ledger_maps_cogs_journal') }}
            </b-link>
          </div>
        </article>
      </b-card>

      <div
        v-if="canPost"
        class="ledger-map-sticky d-md-none"
      >
        <div class="ledger-num small mb-50">
          {{ qty(item.qty_on_hand) }} · {{ money(item.inventory_value) }}
        </div>
        <div class="d-flex flex-wrap">
          <b-button
            v-for="action in actions"
            :key="action"
            size="sm"
            class="mr-50 mb-50 rounded-pill"
            :variant="panel === action ? 'primary' : 'outline-primary'"
            @click="panel = panel === action ? null : action"
          >
            {{ $t(`ledger_maps_action_${action}`) }}
          </b-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BCard, BBadge, BButton, BRow, BCol, BFormGroup, BFormInput, BAlert, BLink, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  getMapStockItem, getMapStockMovements, postMapOpening, postMapPurchase, postMapSale, postMapWriteOff,
} from '@/api/accounting/mapInventory'
import { fetchAllOperationalAccounts } from '@/api/accounting/accounts'
import {
  oneFrom, listFrom, enumValue, enumLabel, formatLedgerMoney, formatLedgerQty,
  roundMoney, roundQty, todayIso, apiErrorMessage,
} from '@/utils/ledger'

const TYPE_VARIANT = {
  opening: 'info',
  purchase: 'primary',
  sale: 'success',
  write_off: 'danger',
}

export default {
  name: 'MapStockShow',
  components: {
    BCard,
    BBadge,
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BAlert,
    BLink,
    BSpinner,
    vSelect,
  },
  data() {
    return {
      loading: false,
      acting: false,
      loadingTills: false,
      tills: [],
      item: null,
      movements: [],
      panel: null,
      saleResult: null,
      showLinksHint: false,
      form: {
        quantity: null,
        unit_cost: null,
        unit_price: null,
        account_id: null,
        movement_date: todayIso(),
        description: '',
      },
      actions: ['opening', 'purchase', 'sale', 'write_off'],
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    canPost() {
      return this.hasPermission('post map_inventory')
    },
    canOpenJournal() {
      return this.hasPermission('view journal_entries')
    },
    needsTill() {
      return this.panel === 'purchase' || this.panel === 'sale'
    },
    canSubmit() {
      const quantity = Number(this.form.quantity)
      if (!Number.isFinite(quantity) || quantity <= 0) return false
      if (this.panel === 'opening' || this.panel === 'purchase') {
        if (!Number.isFinite(Number(this.form.unit_cost)) || Number(this.form.unit_cost) < 0) return false
      }
      if (this.panel === 'sale') {
        if (!Number.isFinite(Number(this.form.unit_price)) || Number(this.form.unit_price) < 0) return false
      }
      if (this.needsTill && !this.form.account_id) return false
      return true
    },
    wacPreview() {
      if (this.panel !== 'purchase' || !this.item) return null
      const qtyOnHand = Number(this.item.qty_on_hand) || 0
      const currentCost = Number(this.item.unit_cost) || 0
      const addQty = Number(this.form.quantity)
      const addCost = Number(this.form.unit_cost)
      if (!Number.isFinite(addQty) || addQty <= 0 || !Number.isFinite(addCost)) return null
      const nextQty = qtyOnHand + addQty
      if (nextQty <= 0) return null
      return roundMoney(((qtyOnHand * currentCost) + (addQty * addCost)) / nextQty)
    },
    salePreview() {
      if (this.panel !== 'sale' || !this.item) return null
      const quantity = Number(this.form.quantity)
      const price = Number(this.form.unit_price)
      const cost = Number(this.item.unit_cost) || 0
      if (!Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(price)) return null
      return roundMoney((price - cost) * quantity)
    },
  },
  watch: {
    panel(value) {
      this.form = this.emptyForm()
      if (value === 'purchase' || value === 'sale') this.loadTills()
    },
    '$route.params.id': 'load',
  },
  mounted() {
    this.load()
  },
  methods: {
    emptyForm() {
      return {
        quantity: null,
        unit_cost: null,
        unit_price: null,
        account_id: null,
        movement_date: todayIso(),
        description: '',
      }
    },
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    qty(value) {
      return formatLedgerQty(value || 0, this.$i18n.locale)
    },
    axis(value, kind) {
      const key = `ledger_maps_${kind}_${value}`
      return value && this.$te(key) ? this.$t(key) : (value || '')
    },
    quadrant(row) {
      if (!row) return '—'
      const ns = this.axis(row.north_south, 'ns')
      const ew = this.axis(row.east_west, 'ew')
      return [ns, ew].filter(Boolean).join(' / ') || '—'
    },
    tillLabel(account) {
      if (!account) return ''
      const code = account.code ? `${account.code} — ` : ''
      return `${code}${account.name || ''}`
    },
    typeText(type) {
      return enumLabel(type, this.$i18n.locale)
    },
    typeVariant(type) {
      return TYPE_VARIANT[enumValue(type)] || 'secondary'
    },
    journalId(row, kind) {
      if (!row) return null
      if (kind === 'revenue') {
        return row.revenue_journal_entry_id || (row.revenue_journal_entry && row.revenue_journal_entry.id) || null
      }
      return row.cogs_journal_entry_id || (row.cogs_journal_entry && row.cogs_journal_entry.id) || null
    },
    journalRoute(id) {
      return { name: 'ledger-journals-view', params: { id } }
    },
    saleMargin(row) {
      const quantity = Number(row.quantity) || 0
      const price = Number(row.unit_price) || 0
      const cost = Number(row.unit_cost) || 0
      return roundMoney((price - cost) * quantity)
    },
    toast(title, variant) {
      const icons = {
        success: 'CheckIcon',
        warning: 'AlertTriangleIcon',
        danger: 'AlertCircleIcon',
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: icons[variant] || 'InfoIcon',
          variant,
        },
      })
    },
    async load() {
      this.loading = true
      this.saleResult = null
      this.showLinksHint = false
      try {
        const res = await getMapStockItem(this.$route.params.id)
        this.item = oneFrom(res)
        await this.loadMovements()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async loadMovements() {
      const embedded = this.item && Array.isArray(this.item.movements) ? this.item.movements : null
      if (embedded) {
        this.movements = embedded.slice(0, 8)
        return
      }
      const res = await getMapStockMovements({ cadastral_map_id: this.$route.params.id })
      this.movements = listFrom(res).slice(0, 8)
    },
    async loadTills() {
      if (this.tills.length || this.loadingTills) return
      this.loadingTills = true
      try {
        const rows = await fetchAllOperationalAccounts({ status: 'active', per_page: 100 })
        this.tills = rows.filter(account => {
          const type = enumValue(account.type)
          return type === 'cash' || type === 'bank' || type === 'petty_cash'
        })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loadingTills = false
      }
    },
    body() {
      const payload = {
        quantity: roundQty(this.form.quantity),
      }
      if (this.form.movement_date) payload.movement_date = this.form.movement_date
      const description = String(this.form.description || '').trim()
      if (description) payload.description = description
      if (this.panel === 'opening' || this.panel === 'purchase') {
        payload.unit_cost = roundMoney(this.form.unit_cost)
      }
      if (this.panel === 'sale') payload.unit_price = roundMoney(this.form.unit_price)
      if (this.needsTill) payload.account_id = this.form.account_id
      return payload
    },
    async confirmPost() {
      if (!this.canPost || !this.canSubmit || this.acting) return
      const result = await Swal.fire({
        text: this.$t(`ledger_maps_confirm_${this.panel}`),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t(`ledger_maps_action_${this.panel}`),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.acting = true
      this.showLinksHint = false
      try {
        const payload = this.body()
        const callers = {
          opening: postMapOpening,
          purchase: postMapPurchase,
          sale: postMapSale,
          write_off: postMapWriteOff,
        }
        const res = await callers[this.panel](this.item.id, payload)
        const movement = oneFrom(res)
        if (this.panel === 'sale') this.saleResult = movement
        this.toast(this.$t('ledger_maps_posted'), 'success')
        this.panel = null
        const fresh = await getMapStockItem(this.item.id)
        this.item = oneFrom(fresh)
        await this.loadMovements()
      } catch (error) {
        const status = error?.response?.status
        const message = apiErrorMessage(error, this.$t('ledger_error'))
        this.toast(message, status === 422 ? 'warning' : 'danger')
        if (status === 422) {
          const text = String(message || '').toLowerCase()
          const unmapped = text.includes('ربط') || text.includes('unmapped') || text.includes('link')
          this.showLinksHint = unmapped && this.hasPermission('edit gl_links')
          const fresh = await getMapStockItem(this.item.id)
          this.item = oneFrom(fresh)
        }
      } finally {
        this.acting = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-map-stock {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}
.ledger-map-card {
  border-bottom: 1px solid #eee;
  padding: 0.75rem 0.15rem;
}
.ledger-map-sticky {
  position: fixed;
  bottom: 0;
  inset-inline: 0;
  z-index: 20;
  background: #fff;
  border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
